import React, { useEffect } from 'react';
import Header from '../common/Header';
import Footer from '../common/Footer';
import { useSelector, useDispatch } from 'react-redux';
import { Navigate } from 'react-router-dom';
import { useCookieConsent } from '../../hooks/useCookieConsent';
import CookieConsent from '../common/CookieConsent';
import { fetchContestStats, fetchDailyContent } from '../../store/thunks/contestThunks';
import GuessThePhoto from '../contest/GuessThePhoto';
import DailyTrivia from '../contest/DailyTrivia';
import ContactUs from '../layout/ContactUs';
import LeaderBoard from '../contest/LeaderBoard';
import EntriesLog from '../contest/EntriesLog';
import { fetchDailyTrivia } from '../../store/thunks/triviaThunks';
import { fetchUserEntries, checkDailyVisit } from '../../store/thunks/entriesThunk';
import Qualifiers from '../contest/Qualifiers';
import Profile from '../contest/Porfile';

function classNames(...classes) {
    return classes.filter(Boolean).join(' ')
}

const ContestPage = () => {
    const { isAuthenticated } = useSelector(state => state.auth);
    const { hasConsent, hasAnalyticsConsent, hasPreferencesConsent } = useCookieConsent();
    const dispatch = useDispatch();
    const { stats, statsLoading, statsError } = useSelector((state) => state.contest);
    const { entries, dailyContent } = useSelector((state) => state.contest);

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Await the completion of checkDailyVisit
                await dispatch(checkDailyVisit()).unwrap();

                // Now proceed with the other dispatches
                dispatch(fetchContestStats());
                dispatch(fetchDailyContent());
                dispatch(fetchDailyTrivia());
                dispatch(fetchUserEntries());
            } catch (error) {
                console.error('Error during checkDailyVisit or subsequent actions:', error);
            }
        };

        fetchData();
    }, [dispatch]);


    useEffect(() => {
        if (hasAnalyticsConsent) {
            // Initialize analytics
        }

        if (hasPreferencesConsent) {
            // Load user preferences
        }
    }, [hasAnalyticsConsent, hasPreferencesConsent]);

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    return (
        <div>
            <Header />
            <div className="relative z-10 py-12 px-4 sm:px-6 lg:px-8 bg-gray-100">
                <div className="max-w-8xl mx-auto">
                    {/* Main Content */}
                    <div className="flex flex-col lg:flex-row gap-8">
                        {/* Leaderboard - Left column on desktop, top on mobile */}
                        <div className="w-full lg:w-1/4 order-1 lg:order-1">
                            <Profile />
                            <LeaderBoard />
                            <Qualifiers />
                        </div>

                        {/* Contest Content - Middle column */}
                        <div className="w-full lg:w-1/2 order-2 lg:order-2">
                            <div className="text-center bg-white bg-opacity-90 p-8 rounded-lg">
                                <h1 className="font-display text-4xl font-extrabold text-gray-900 sm:text-5xl md:text-6xl tracking-tight">
                                    Virgin Voyages Cruise Contest
                                </h1>
                                <p className="mt-3 max-w-md mx-auto font-body text-base text-gray-600 sm:text-lg md:mt-5 md:text-xl md:max-w-3xl">
                                    Win a luxurious cruise aboard the Scarlet Lady! Experience the Fire & Sunset Soirées departing December 10th, 2025.
                                </p>
                            </div>

                            <div className="mt-10">
                                {statsLoading ? (
                                    <div className="text-center border border-gray-200 rounded-lg p-4">Loading stats...</div>
                                ) : statsError ? (
                                    <div className="text-center text-red-600 border border-red-200 rounded-lg p-4">Error loading stats: {statsError}</div>
                                ) : stats ? (
                                    <dl className="mx-auto grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        {stats.map((stat) => (
                                            <div
                                                key={stat.name}
                                                className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-2 bg-white px-4 py-10 sm:px-6 xl:px-8 border border-gray-200 rounded-lg"
                                            >
                                                <dt className="font-body text-sm/6 font-medium text-gray-500">{stat.name}</dt>
                                                <dd
                                                    className={classNames(
                                                        stat.changeType === 'negative' ? 'text-rose-600' : 'text-emerald-600',
                                                        'font-body text-xs font-medium',
                                                    )}
                                                >
                                                    {stat.change}
                                                </dd>
                                                <dd className="w-full flex-none font-display text-3xl/10 font-medium tracking-tight text-gray-900">
                                                    {stat.value}
                                                </dd>
                                            </div>
                                        ))}
                                    </dl>
                                ) : null}
                            </div>

                            <section className="mt-10">
                                <GuessThePhoto />
                            </section>
                            <section className="mt-10">
                                <DailyTrivia />
                            </section>
                            <section className="mt-10">
                                <EntriesLog />
                            </section>
                        </div>

                        {/* Ads - Right column on desktop, bottom on mobile */}
                        <div className="w-full lg:w-1/4 order-3 lg:order-3">
                            <div className="flex flex-col gap-4">
                                <div className="bg-white rounded-lg p-4 h-96 shadow-sm flex items-center justify-center text-gray-400 cursor-pointer" onClick={() => window.open('https://www.virginvoyages.com', '_blank')}>
                                    <img
                                        src="/vvCanadaOffer.jpg"
                                        alt="Virgin Voyages"
                                        className="object-contain max-h-full max-w-full"
                                        onClick={() => window.open('https://www.virginvoyages.com/book/voyage-planner/find-a-voyage?cabins=1&currencyCode=CAD&agentId=104397&agencyId=451&bookingChannel=FMLINK', '_blank')}
                                        style={{ cursor: 'pointer' }}
                                    />
                                </div>
                                <div className="bg-white rounded-lg p-4 h-96 shadow-sm flex items-center justify-center text-gray-400 cursor-pointer" onClick={() => window.open('https://www.virginvoyages.com', '_blank')}>
                                    <img
                                        src="/cruiseCompare.jpg"
                                        alt="Cruise Compare"
                                        className="object-contain max-h-full max-w-full"
                                        onClick={() => window.open('https://www.cruisecompare.ca/?utm_source=vvcontest&utm_medium=banner&utm_campaign=contest_ad', '_blank')}
                                        style={{ cursor: 'pointer' }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <ContactUs />
            <div className="h-16" />
            {!hasConsent && <CookieConsent />}
        </div>
    );
};

export default ContestPage;
